/**
 * CriteriaChipInput.jsx — Employer v1.1 Phase 0.6 Slice B.
 *
 * Chip-input control for required_criteria / preferred_criteria.
 * Mirrors backend caps (MAX_CRITERIA=60, MAX_CRITERION_LEN=200) so
 * the user gets clean inline feedback BEFORE PATCH /criteria fires.
 *
 * Enter or comma → adds chip. Backspace on empty input → removes
 * last chip. Click × on a chip → removes. Trim + case-insensitive
 * dedupe mirrors `_normalise_criteria_list` in core.emp_role_criteria.
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

export const MAX_CRITERIA      = 60;
export const MAX_CRITERION_LEN = 200;

function normaliseAdd(items, raw) {
  const s = String(raw || '').trim();
  if (!s) return { next: items, error: null };
  if (s.length > MAX_CRITERION_LEN) {
    return { next: items, error: `Max ${MAX_CRITERION_LEN} characters per entry` };
  }
  const lowerSet = new Set(items.map((x) => x.toLowerCase()));
  if (lowerSet.has(s.toLowerCase())) {
    return { next: items, error: null };
  }
  if (items.length >= MAX_CRITERIA) {
    return { next: items, error: `Maximum of ${MAX_CRITERIA} entries reached` };
  }
  return { next: [...items, s.slice(0, MAX_CRITERION_LEN)], error: null };
}

export default function CriteriaChipInput({
  value,
  onChange,
  placeholder,
  tone = 'emerald',
  dataTestid,
}) {
  const items = Array.isArray(value) ? value : [];
  const [buffer, setBuffer] = useState('');
  const [error,  setError]  = useState(null);

  function commitFromBuffer(raw) {
    const { next, error: err } = normaliseAdd(items, raw);
    setError(err);
    if (next !== items) onChange(next);
    setBuffer('');
  }

  function removeAt(idx) {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
    setError(null);
  }

  function handleKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitFromBuffer(buffer);
    } else if (e.key === 'Backspace' && buffer === '' && items.length > 0) {
      e.preventDefault();
      removeAt(items.length - 1);
    }
  }

  const chipClass =
    tone === 'sky'
      ? 'bg-sky-500/10 text-sky-200'
      : 'bg-emerald-500/10 text-emerald-200';

  return (
    <div className="space-y-2" data-testid={dataTestid || 'criteria-chip-input'}>
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1.5">
        {items.map((c, i) => (
          <Badge
            key={`${c}-${i}`}
            className={`${chipClass} border-transparent`}
            data-testid={`${dataTestid || 'criteria'}-chip-${i}`}
          >
            <span className="truncate max-w-[200px]">{c}</span>
            <button
              type="button"
              aria-label={`Remove ${c}`}
              className="ml-1 rounded hover:bg-black/10"
              onClick={() => removeAt(i)}
              data-testid={`${dataTestid || 'criteria'}-chip-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          type="text"
          className="border-0 h-7 flex-1 min-w-[120px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          value={buffer}
          onChange={(e) => { setBuffer(e.target.value); setError(null); }}
          onKeyDown={handleKey}
          onBlur={() => buffer && commitFromBuffer(buffer)}
          placeholder={placeholder || 'Type and press Enter…'}
          maxLength={MAX_CRITERION_LEN + 1}
          data-testid={`${dataTestid || 'criteria'}-input`}
        />
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className={error ? 'text-rose-200' : 'text-slate-300'}>
          {error
            ? error
            : `${items.length} / ${MAX_CRITERIA} entries · ${MAX_CRITERION_LEN} char max each`}
        </span>
      </div>
    </div>
  );
}
